<template>
  <div
    class="app-border-card-class flex items-center gap-3 px-3.5 py-2 cursor-pointer transition-all duration-200 border-l-[6px]! mt-1.5 mx-2 rounded-lg shadow-sm"
    :style="{ borderLeftColor: statusAccentColor }"
    :class="[
      isSelected
        ? 'bg-[var(--p-primary-200)]/50  transition-all duration-200 hover:shadow-md dark:bg-[var(--p-primary-800)]/40 border-y-primary-100/50 dark:border-y-primary-950/20 '
        : '  hover:bg-[var(--p-primary-100)]/50 transition-all duration-200 hover:shadow-md dark:hover:bg-[var(--p-primary-800)]/20',
    ]"
    @click="$emit('select')"
  >
    <div class="flex-shrink-0">
      <div
        class="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm bg-gradient-to-br shadow-xs"
        :class="avatarColor"
      >
        {{ contactInitials }}
      </div>
    </div>

    <div class="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] gap-2">
      <div class="min-w-0">
        <h3
          class="truncate text-sm font-medium text-[var(--p-surface-800)] dark:text-[var(--p-surface-0)]"
        >
          {{ displayName }}
          <span
            v-if="usernameLabel"
            class="font-normal text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-0)]/70"
          >
            {{ usernameLabel }}
          </span>
        </h3>

        <div v-if="chatStatus === 'TRANSFERRING'" class="mt-1">
          <span
            class="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/50 dark:text-blue-300"
          >
            Transferencia
          </span>
        </div>
        <div v-else-if="chatStatus === 'TRANSFER_REJECTED'" class="mt-1">
          <span
            class="inline-flex items-center rounded-full border border-red-100 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:border-red-900/40 dark:bg-red-950/50 dark:text-red-300"
          >
            Rechazada
          </span>
        </div>

        <p
          class="truncate text-xs text-[var(--p-surface-650)] dark:text-[var(--p-primary-50)]/90 leading-tight"
          :class="{
            'font-medium text-[var(--p-surface-900)] dark:text-white': unreadCount > 0,
          }"
        >
          {{ cleanLastMessage }}
        </p>

        <div
          v-if="campaignName"
          class="mt-1 inline-flex max-w-full items-center rounded-full bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300 border border-primary-100/60 dark:border-primary-800/30 px-2 py-0.5 gap-1 text-[10px] font-medium leading-none"
        >
          <IconCampaign width="10" height="10" class="text-primary-600 dark:text-primary-400 mt-1" />
          <span class="truncate">{{ campaignName }}</span>
        </div>
      </div>

      <div class="flex min-w-fit flex-col items-end justify-between gap-1.5">
        <span class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-0)]/90">
          {{ formattedTime }}
        </span>
        <span
          v-if="unreadCount > 0"
          class="flex min-h-5 min-w-5 items-center justify-center rounded-full bg-[var(--p-primary-color)] px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-white shadow-sm animate-pulse"
        >
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>

        <div class="flex items-center justify-end gap-1.5 mt-auto">
          <span
            v-if="waitingTime"
            class="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md font-medium transition-all duration-300"
            :class="[
              { 'animate-pulse': waitingTime.shouldPulse },
              getWaitingTimeClasses(waitingTime),
            ]"
          >
            <Timer :size="11" :stroke-width="2.5" />
            {{ waitingTime.label }}
          </span>
          <CheckCheck
            v-if="messageStatus === 'READ'"
            :size="12"
            class="text-[var(--p-primary-color)]"
          />
          <CheckCheck
            v-else-if="messageStatus === 'DELIVERED'"
            :size="12"
            class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-200)]"
          />
          <Check
            v-else-if="messageStatus === 'SEND' || messageStatus === 'SENT'"
            :size="12"
            class="text-[var(--p-surface-400)]"
          />
          <Clock
            v-else-if="messageStatus === 'ENQUEUED' || messageStatus === 'PENDING'"
            :size="12"
            class="text-[var(--p-surface-300)]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Check, CheckCheck, Clock, Timer } from 'lucide-vue-next'
import type { MessageStatus } from '../../types/enums'
import { ChatStatus } from '../../types/enums'
import { getAvatarColor } from '../../services/constants'
import type { WaitingTimeInfo } from '../../types/interfaces'
import { parseUtcDate } from '../../composables/useTimeFormatter'
import IconCampaign from '@/assets/svg/campaign_icon.svg?component'
import { formatUsername } from '../../services/contactIdentity'

interface Props {
  contactName: string
  contactProfileName?: string | null
  contactUsername?: string | null
  lastMessage: string
  lastMessageTime?: string
  unreadCount: number
  isSelected: boolean
  messageStatus?: MessageStatus | null
  waitingTime?: WaitingTimeInfo | null
  campaignName?: string
  chatStatus?: ChatStatus
  channelId?: string
  isMyExpiredChat?: boolean
}

const props = defineProps<Props>()
defineEmits<{
  select: []
}>()

const displayName = computed(() => {
  return props.contactName?.trim() || props.contactProfileName?.trim() || ''
})

// El username va junto al nombre de perfil, salvo que ya sea el nombre (el
// cliente no puso ninguno).
const usernameLabel = computed(() => {
  const username = formatUsername(props.contactUsername)
  return username !== displayName.value ? username : ''
})

const contactInitials = computed(() => {
  const names = displayName.value.split(' ')
  return names
    .slice(0, 2)
    .map((name) => name.charAt(0).toUpperCase())
    .join('')
})

const avatarColor = computed(() => {
  return getAvatarColor(displayName.value)
})

const cleanLastMessage = computed(() => {
  return props.lastMessage
    .replace(/```(?=\S)([\s\S]*?\S)```/g, '$1')
    .replace(/\*(?=\S)([^*\n]*?\S)\*/g, '$1')
    .replace(/_(?=\S)([^_\n]*?\S)_/g, '$1')
    .replace(/~(?=\S)([^~\n]*?\S)~/g, '$1')
})

const statusAccentColor = computed(() => {
  switch (props.chatStatus) {
    case ChatStatus.PENDING:
      return '#8A7CB8'
    case ChatStatus.ATTENTION:
      return '#F88C00'
    case ChatStatus.ABANDON:
      return '#DA002C'
    case ChatStatus.EXPIRE:
      return props.isMyExpiredChat ? '#0EA5E9' : '#4A5565'
    case ChatStatus.TEMPLATE:
      return '#FFD900'
    default:
      return 'color-mix(in srgb, var(--p-primary-color) 50%, transparent)'
  }
})

const formattedTime = computed(() => {
  if (!props.lastMessageTime) return ''

  const date = parseUtcDate(props.lastMessageTime)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

  if (diffInHours < 24) {
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  } else if (diffInHours < 48) {
    return 'Ayer'
  } else {
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })
  }
})

const getWaitingTimeClasses = (waitingTime: WaitingTimeInfo): string => {
  switch (waitingTime.severity) {
    case 'success':
      return 'bg-emerald-50/80 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400'
    case 'info':
      return 'bg-blue-50/80 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400'
    case 'warn':
      return 'bg-amber-50/80 dark:bg-amber-950/20 text-amber-600 dark:text-amber-400'
    case 'danger':
      return 'bg-orange-50/80 dark:bg-orange-950/20 text-orange-600 dark:text-orange-400'
    case 'critical':
      return 'bg-red-50/80 dark:bg-red-950/30 text-red-600 dark:text-red-400'
    default:
      return 'bg-gray-50 dark:bg-gray-950/20 text-gray-600 dark:text-gray-400'
  }
}
</script>
